// Admin top-strip: at-a-glance count of pending approvals across the platform.
// Tap any chip → jumps to the queue for that kind. Auto-refreshes every 45s.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

// Which admin route each pending kind should open when tapped.
const KIND_META = {
  fund_deposit:      { emoji: '💰', label: 'Fund',      to: '/admin/fund-requests' },
  at_deposit:        { emoji: '🪙', label: 'AT',        to: '/admin/smart-panel?kind=at_deposit' },
  marketplace_order: { emoji: '📦', label: 'Orders',    to: '/admin/marketplace-orders' },
  support:           { emoji: '🆘', label: 'Support',   to: '/admin/support' },
  delivery_partner:  { emoji: '🛵', label: 'Delivery',  to: '/admin/delivery-partners' },
  tool_activation:   { emoji: '🔓', label: 'Activation',to: '/admin/smart-panel?kind=tool_activation' },
  renewal:           { emoji: '🔁', label: 'Renewal',   to: '/admin/smart-panel?kind=renewal' },
  kyc:               { emoji: '🪪', label: 'KYC',       to: '/admin/users?kyc=pending' },
};

export default function AdminPendingBanner() {
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [collapsed, setCollapsed] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const r = await api.get('/admin/smart-panel/feed', { params: { limit: 1 } });
      setCounts(r.data?.counts_by_kind || {});
      setTotal(r.data?.total || 0);
    } catch (e) { /* silent — banner just hides */ }
  }, []);

  useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, 45000);
    return () => clearInterval(id);
  }, [fetchCounts]);

  if (!total || total === 0) return null;

  const chips = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div data-testid="admin-pending-banner"
         className="sticky top-0 z-20 bg-gradient-to-r from-amber-500/95 via-orange-500/95 to-rose-500/95 backdrop-blur border-b border-amber-300/40 shadow-lg">
      <div className="flex items-center gap-2 px-3 py-1.5 overflow-x-auto">
        <button onClick={() => setCollapsed((v) => !v)}
                data-testid="admin-pending-banner-toggle"
                className="shrink-0 flex items-center gap-1.5 text-slate-900 font-black text-xs">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-amber-200 text-[11px] font-black animate-pulse">
            {total}
          </span>
          <span>Pending Approvals</span>
          <span className="text-[10px] opacity-70">{collapsed ? '▶' : '▼'}</span>
        </button>
        {!collapsed && chips.map(([kind, n]) => {
          const m = KIND_META[kind] || { emoji: '📋', label: kind, to: '/admin/smart-panel' };
          return (
            <Link key={kind} to={m.to}
                  data-testid={`admin-pending-chip-${kind}`}
                  className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/80 hover:bg-slate-900 text-white text-[11px] font-black transition">
              <span>{m.emoji}</span>
              <span>{m.label}</span>
              <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[10px]">{n}</span>
            </Link>
          );
        })}
        {!collapsed && (
          <Link to="/admin/smart-panel" data-testid="admin-pending-open-panel"
                className="shrink-0 ml-auto px-2 py-1 rounded bg-slate-900 text-amber-200 text-[11px] font-black">
            Open Panel →
          </Link>
        )}
      </div>
    </div>
  );
}
